import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { modalConfig } from '../../../../config/ui/modals/modal-config';

export interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title */
  title?: string;
  /** Modal variant (optional) */
  variant?: string;
  /** Icon to display in header (optional) */
  icon?: React.ReactNode;
  /** Footer content (optional) */
  footer?: React.ReactNode;
  /** Modal size */
  size?: keyof typeof modalConfig.sizes;
  /** Whether to show close button in header */
  showCloseButton?: boolean;
  /** Whether modal can be closed by clicking overlay */
  closeOnOverlayClick?: boolean;
  /** Whether modal can be closed with Escape key */
  closeOnEscape?: boolean;
  /** Custom class name */
  className?: string;
  /** Custom styles */
  style?: React.CSSProperties;
  /** Children content */
  children: React.ReactNode;
}

/**
 * Simple Modal Component for System Settings
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  variant,
  icon,
  footer,
  size = 'medium',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = '',
  style = {},
  children,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  // Handle Escape key
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [closeOnEscape, isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (closeOnOverlayClick && event.target === overlayRef.current) {
      onClose();
    }
  };

  const handleCloseClick = () => {
    onClose();
  };

  const modalContent = (
    <div
      ref={overlayRef}
      style={{
        ...modalConfig.overlay.default,
        zIndex: modalConfig.zIndex.overlay,
      }}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={className}
        style={{
          ...modalConfig.container.default,
          ...modalConfig.sizes[size],
          ...style,
          zIndex: modalConfig.zIndex.modal,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showCloseButton || icon) && (
          <div style={modalConfig.header.default}>
            {icon && (
              <div style={{ marginBottom: '12px', marginRight: '12px', display: 'flex', justifyContent: 'center' }}>
                {icon}
              </div>
            )}
            {title && (
              <h2 style={modalConfig.header.title}>
                {title}
              </h2>
            )}

            {showCloseButton && (
              <button
                style={{
                  position: 'absolute',
                  top: '14px',
                  right: '14px',
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  lineHeight: 1,
                  cursor: 'pointer',
                  padding: '8px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  color: '#6b7280',
                }}
                onClick={handleCloseClick}
                aria-label="Close modal"
                type="button"
              >
                ×
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div style={modalConfig.body.default}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Render in portal
  return ReactDOM.createPortal(modalContent, document.body);
};

/**
 * ModalButton Component
 * Styled button for use in modal footers
 */
export interface ModalButtonProps {
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'danger';
  /** Button type */
  type?: 'button' | 'submit' | 'reset';
  /** Form ID for submit buttons */
  form?: string;
  /** Click handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Whether button is disabled */
  isDisabled?: boolean;
  /** Whether button is in loading state */
  isLoading?: boolean;
  /** Custom class name */
  className?: string;
  /** Custom styles */
  style?: React.CSSProperties;
  /** Button content */
  children: React.ReactNode;
}

export const ModalButton: React.FC<ModalButtonProps> = ({
  variant = 'primary',
  type = 'button',
  form,
  onClick,
  isDisabled = false,
  isLoading = false,
  className = '',
  style = {},
  children,
}) => {
  const variantStyles = {
    primary: {
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      border: 'none',
    },
    secondary: {
      backgroundColor: '#e5e7eb',
      color: '#1f2937',
      border: '1px solid #d1d5db',
    },
    danger: {
      backgroundColor: '#ef4444',
      color: '#ffffff',
      border: 'none',
    },
  };

  const baseStyle: React.CSSProperties = {
    padding: '10px 20px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: isDisabled || isLoading ? 'not-allowed' : 'pointer',
    opacity: isDisabled || isLoading ? 0.6 : 1,
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    ...variantStyles[variant],
    ...style,
  };

  return (
    <button
      type={type}
      form={form}
      onClick={onClick}
      disabled={isDisabled || isLoading}
      className={className}
      style={baseStyle}
    >
      {isLoading && (
        <span style={{
          display: 'inline-block',
          width: '14px',
          height: '14px',
          border: '2px solid currentColor',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 0.6s linear infinite',
        }} />
      )}
      {children}
    </button>
  );
};

export default Modal;